import { useEffect, useState } from 'react';
import { useForm, Controller, useController } from 'react-hook-form';
import ConfirmationModal from '../../../../../../common/components/xepp/confirmation-modal/ConfirmationModal';
import { NotificationFormProps, StopMethod } from './types';
import ToggleSlider from '../../../../../../common/components/xepp/toggle-slider/ToggleSlider';
import ImageUpload from '../../../../../../common/components/xepp/image-upload/ImageUpload';
import AddButtonSection, { ADD_BUTTON_DEFAULTS, AddButtonFields } from './AddButtonSection';
import styles from './NotificationForm.module.scss';
import { useLazyGetBroadcastNotificationDetailQuery } from '../../../../../../service/notification-service';
import { BroadcastNotificationSubType } from '../../../../../../model/notifications/notifications';
import CircularProgressIndicator from '../../../../../../common/components/circular-progress-indicator/CircularProgressIndicator';

interface BannerNotificationFormValues extends AddButtonFields {
    turnOn: boolean;
    image: File | null;
    stopMethod: StopMethod | null;
}

const STOP_METHODS: { value: StopMethod; label: string; description: string }[] = [
    {
        value: 'repeat',
        label: 'Repeat',
        description: 'Display the banner on each login until manually disabled from the console.',
    },
    {
        value: 'viewOnce',
        label: 'View Once',
        description: 'Stop displaying the banner after the user closes it manually once.',
    },
    {
        value: 'dontShowAgain',
        label: "Don't Show Again",
        description: 'The banner stops displaying only when the user selects "Don\'t Show Again" option.',
    },
];

const STOP_METHOD_MAP: Record<number, StopMethod> = {
    1: 'repeat',
    2: 'viewOnce',
    3: 'dontShowAgain',
};


const BannerNotificationForm = ({ onSubmit, onEdit, onExistingChange }: NotificationFormProps) => {
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [pendingData, setPendingData] = useState<BannerNotificationFormValues | null>(null);
    const [imageChanged, setImageChanged] = useState(false);

    const [fetchDetail, { data, isLoading }] = useLazyGetBroadcastNotificationDetailQuery();

    const { control, handleSubmit, reset } = useForm<BannerNotificationFormValues>({
        defaultValues: {
            turnOn: true,
            image: null,
            ...ADD_BUTTON_DEFAULTS,
            stopMethod: null,
        },
    });

    useEffect(() => {
        fetchDetail({ type: BroadcastNotificationSubType.BannerNotification });
    }, []);

    useEffect(() => {
        if (!data) return;
        onExistingChange?.(!!data.id);
        setImageChanged(false);
        reset({
            turnOn: data.turnOnOff ?? false,
            image: null,
            stopMethod: data.stopMethod != null ? (STOP_METHOD_MAP[data.stopMethod] ?? null) : null,
            addButton: data.bannerLinking ?? false,
            buttonLabel: data.buttonLabel ?? '',
            linkScreen: data.bannerUrl ?? '',
        });
    }, [data]);

    const { field: stopMethodField, fieldState: stopMethodFieldState } = useController({
        control,
        name: 'stopMethod',
        rules: { validate: (value) => !!value || 'Please select a stop method.' },
    });

    const onFormSubmit = (data: BannerNotificationFormValues) => {
        setPendingData(data);
        setIsConfirmOpen(true);
    };

    const handleConfirm = () => {
        if (!pendingData) return;
        setIsConfirmOpen(false);
        const payload = { notificationType: 'banner' as const, ...pendingData };
        if (data?.id && onEdit) {
            onEdit(data.id, payload);
        } else {
            onSubmit(payload);
        }
        setPendingData(null);
    };

    if (isLoading) {
        return <div className={styles.loaderWrapper}><CircularProgressIndicator width={24} height={24} /></div>;
    }

    return (
        <>
        <form id="notification-form" className={styles.form} onSubmit={handleSubmit(onFormSubmit)}>
            <Controller
                name="turnOn"
                control={control}
                render={({ field }) => (
                    <div className={styles.toggleRow}>
                        <div className={styles.toggleRowMain}>
                            <span className={styles.toggleLabel}>Turn On/Off</span>
                            <ToggleSlider isActive={field.value} onChange={field.onChange} />
                        </div>
                    </div>
                )}
            />
            <Controller
                name="image"
                control={control}
                rules={{ validate: (value) => !!value || (!imageChanged && !!data?.id) || 'Image is required' }}
                render={({ field: imageField, fieldState: imageFieldState }) => (
                    <div className={styles.field}>
                        <ImageUpload
                            onChange={imageField.onChange}
                            onUserChange={() => setImageChanged(true)}
                            initialImageUrl={data?.image}
                        />
                        {imageFieldState.error && (
                            <span className={styles.errorText}>{imageFieldState.error.message}</span>
                        )}
                    </div>
                )}
            />
            <AddButtonSection
                control={control as any}
                label="Banner Linking"
                description="Add a link or choose a screen to redirect users when they tap the banner."
                showButtonLabel={false}
            />
            <div className={styles.field}>
                <label className={styles.label}>Stop Methods</label>
                {STOP_METHODS.map((method) => (
                    <div key={method.value} className={styles.toggleRow}>
                        <div className={styles.toggleRowMain}>
                            <span className={styles.toggleLabel}>{method.label}</span>
                            <ToggleSlider
                                isActive={stopMethodField.value === method.value}
                                onChange={() => stopMethodField.onChange(method.value)}
                            />
                        </div>
                        <span className={styles.toggleDescription}>{method.description}</span>
                    </div>
                ))}
                {stopMethodFieldState.error && (
                    <span className={styles.errorText}>{stopMethodFieldState.error.message}</span>
                )}
            </div>
        </form>
        <ConfirmationModal
            isOpen={isConfirmOpen}
            onClose={() => setIsConfirmOpen(false)}
            onConfirm={handleConfirm}
            title="Send Banner Notification"
            message="Send banner notification to all customers?"
            confirmText="Confirm"
            cancelText="Cancel"
            variant="info"
        />
        </>
    );
};

export default BannerNotificationForm;
